import { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Briefcase, CircleDollarSign, BookOpen, ChevronRight, X, Menu, Target, User } from 'lucide-react';
import AuthPanel from './AuthPanel';
import CareerGrowthLogo from './CareerGrowthLogo';
import { clearAuthSession, isAuthenticated } from '../utils/auth';
import { pushDataLayer } from '../utils/dataLayer';

export default function SiteHeader({ authModal, showGuestContinue = false }) {
  const navigate = useNavigate();
  const location = useLocation();
  const wasAuthOpenRef = useRef(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const authenticated = isAuthenticated();

  const fullPath = `${location.pathname}${location.search}${location.hash}`;

  const publicNavItems = [
    { key: 'how-it-works', label: 'How it Works', to: '/how-it-works', accent: true, icon: Briefcase },
    { key: 'vacancies', label: 'Start Interview', to: '/search', icon: Briefcase },
    { key: 'blog', label: 'Blog', to: '/blog', icon: BookOpen },
    { key: 'pricing', label: 'Pricing', to: '/pricing', icon: CircleDollarSign },
  ];
  const privateNavItems = [
    { key: 'profile', label: 'Profile', to: '/profile', icon: User },
    { key: 'interview', label: 'My Interviews', to: '/interview', icon: Target },
  ];

  useEffect(() => {
    const isOpenNow = Boolean(authModal?.isOpen);
    if (isOpenNow && !wasAuthOpenRef.current) {
      // Fire after mount so it corresponds to the popup being visible.
      setTimeout(() => {
        pushDataLayer({
          event: 'ui_interaction',
          action: 'show',
          element: 'auth_popup',
          location: 'iframe',
          data_source: 'front',
        });
      }, 0);
    }
    wasAuthOpenRef.current = isOpenNow;
  }, [authModal?.isOpen]);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname, location.search, location.hash]);

  useEffect(() => {
    if (!isMobileMenuOpen) return undefined;
    const { overflow } = document.body.style;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = overflow;
    };
  }, [isMobileMenuOpen]);

  const handlePrimaryButton = () => {
    if (!authenticated) {
      pushDataLayer({
        event: 'login_start',
        location: 'header',
        data_source: 'front',
      });
      authModal.openAuth('search', fullPath);
      return;
    }
    clearAuthSession();
    navigate('/', { replace: true });
  };

  const handleGuestContinue = () => {
    authModal.closeAuth();
    if (authModal.pendingAction === 'interview') {
      navigate('/interview');
      return;
    }
    if (authModal.pendingAction === 'profile') {
      navigate('/profile');
      return;
    }
    navigate('/search');
  };

  const handleNavigate = (to) => {
    setIsMobileMenuOpen(false);
    navigate(to);
  };

  const isActivePath = (to) => location.pathname.startsWith(to);

  const primaryButtonLabel = authenticated ? 'Log out' : 'Log in';

  return (
    <>
      <header className="w-full bg-white/80 backdrop-blur border-b border-gray-100 sticky top-0 z-[100]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <CareerGrowthLogo />
          </Link>

          <nav className="hidden md:flex items-center text-sm text-gray-500">
            <div className="flex items-center gap-8">
              {publicNavItems.map((item) => (
                <button
                  key={item.key}
                  type="button"
                  onClick={() => navigate(item.to)}
                  className={`transition-colors ${isActivePath(item.to)
                    ? 'text-gray-900 font-semibold'
                    : item.accent
                      ? 'text-orange-600 hover:text-orange-700 font-medium'
                      : 'hover:text-gray-800'
                    }`}
                >
                  {item.label}
                </button>
              ))}
            </div>
            {authenticated && (
              <>
                <span className="mx-6 h-5 w-px bg-gray-200" />
                <div className="flex items-center gap-6">
                  {privateNavItems.map((item) => (
                    <button
                      key={item.key}
                      type="button"
                      onClick={() => navigate(item.to)}
                      className={`transition-colors ${isActivePath(item.to) ? 'text-gray-900 font-semibold' : 'hover:text-gray-800'}`}
                    >
                      {item.label}
                    </button>
                  ))}
                </div>
              </>
            )}
          </nav>

          <button
            type="button"
            onClick={handlePrimaryButton}
            className="hidden md:inline-flex btn-orange px-5 py-2 rounded-2xl text-sm"
          >
            {primaryButtonLabel}
          </button>

          <button
            type="button"
            className="md:hidden relative h-10 w-10 rounded-xl border border-gray-200 bg-white p-2 flex items-center justify-center text-gray-700"
            onClick={() => setIsMobileMenuOpen((prev) => !prev)}
            aria-label={isMobileMenuOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={isMobileMenuOpen}
            aria-controls="mobile-main-menu"
          >
            <Menu className="w-5 h-5" />
          </button>
        </div>
      </header>

      {/* Backdrop */}
      <div
        className={`md:hidden fixed inset-0 z-[1000] bg-black/45 transition-opacity duration-300 ${isMobileMenuOpen ? 'opacity-100' : 'pointer-events-none opacity-0'}`}
        onClick={() => setIsMobileMenuOpen(false)}
        aria-hidden={!isMobileMenuOpen}
      />

      {/* Slide-in panel */}
      <aside
        id="mobile-main-menu"
        className={`md:hidden fixed right-0 top-0 bottom-0 z-[1001] w-[min(280px,75vw)] bg-white shadow-2xl transition-transform duration-300 ease-[cubic-bezier(0.32,0.72,0,1)] ${isMobileMenuOpen ? 'translate-x-0' : 'translate-x-full'} flex flex-col`}
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <div className="flex items-center gap-2">
            <CareerGrowthLogo size={28} hideText />
            <span className="font-bold text-[17px] text-[#1E2A3A]">
              Job<span className="text-[#F47920]">Skills</span>
            </span>
          </div>
          <button
            onClick={() => setIsMobileMenuOpen(false)}
            className="w-9 h-9 flex items-center justify-center bg-gray-100 rounded-lg text-gray-700 hover:bg-gray-200 transition-colors"
          >
            <X className="w-[18px] h-[18px]" />
          </button>
        </div>

        <nav className="flex-1 px-3 py-3 overflow-y-auto">
          {publicNavItems.map((item) => (
            <button
              key={`mobile-${item.key}`}
              type="button"
              onClick={() => handleNavigate(item.to)}
              className="w-full flex items-center gap-3 p-3 rounded-xl text-left text-[15px] font-medium text-gray-700 hover:bg-gray-50 transition-colors group"
            >
              <div className="w-9 h-9 flex items-center justify-center bg-gray-100 rounded-lg text-gray-500 group-hover:bg-white group-hover:shadow-sm transition-all">
                <item.icon className="w-[18px] h-[18px]" />
              </div>
              <span className="flex-1">{item.label}</span>
              <ChevronRight className="w-4 h-4 text-gray-400" />
            </button>
          ))}

          {authenticated && (
            <div className="mt-4 border-t border-gray-100 pt-3">
              <p className="px-3 pb-2 text-[11px] font-semibold uppercase tracking-wide text-gray-400">Workspace</p>
              {privateNavItems.map((item) => (
                <button
                  key={`mobile-private-${item.key}`}
                  type="button"
                  onClick={() => handleNavigate(item.to)}
                  className={`w-full flex items-center gap-3 p-3 rounded-xl text-left text-[15px] font-medium transition-colors group ${isActivePath(item.to) ? 'bg-orange-50 text-orange-700' : 'text-gray-700 hover:bg-gray-50'}`}
                >
                  <div className="w-9 h-9 flex items-center justify-center bg-gray-100 rounded-lg text-gray-500 group-hover:bg-white group-hover:shadow-sm transition-all">
                    <item.icon className="w-[18px] h-[18px]" />
                  </div>
                  <span className="flex-1">{item.label}</span>
                  <ChevronRight className="w-4 h-4 text-gray-400" />
                </button>
              ))}
            </div>
          )}
        </nav>

        <div className="p-4 pb-6 border-t border-gray-100">
          <button
            type="button"
            onClick={handlePrimaryButton}
            className="w-full bg-[#F47920] hover:bg-[#E06810] text-white py-3.5 rounded-xl text-[15px] font-semibold transition-colors shadow-sm"
          >
            {primaryButtonLabel}
          </button>
        </div>
      </aside>

      {authModal.isOpen && (
        <div className="fixed inset-0 z-[2000] flex items-end sm:items-center justify-center bg-black/45 transition-opacity">
          <div className="w-full sm:max-w-md">
            <AuthPanel
              mode="modal"
              defaultTab="login"
              onClose={authModal.closeAuth}
              onGuestContinue={showGuestContinue ? handleGuestContinue : undefined}
              redirectTo={authModal.redirectTo}
            />
          </div>
        </div>
      )}
    </>
  );
}
